import React, {useState, useEffect} from 'react';
import {useToast} from '../components/ui/toast';
import {Text} from '../components/ui/text';

import {useAppSelector} from '../state/hooks';

/**
 * Copy version text to the clipboard.
 */
const copyVersion = async (
  s: string,
  showToast: (message: string, type: 'success' | 'error') => void,
) => {
  try {
    await navigator.clipboard.writeText(s);
    showToast('Copied Nudge version info to clipboard!', 'success');
  } catch (e) {
    showToast(`Failed to copy Nudge version: ${e}`, 'error');
  }
};

/**
 * Information about the Nudge API version and status.
 */
export type ApiInfo = Readonly<{
  coreVersion: string;
  pluginVersion: string;
  status: string;
}>;

/**
 * Find info about the Nudge API.
 */
const fetchApiInfo = async (baseUrl: string) => {
  const r = await fetch(`${baseUrl}info`);
  const d = await r.json();
  return d as ApiInfo;
};

/**
 * Hook to find Nudge API info.
 */
const useApiInfo = () => {
  const [error, setError] = useState<Error | null>(null);
  const [fetching, setFetching] = useState(false);
  const [apiInfo, setApiInfo] = useState<ApiInfo | null>(null);
  const gqlUrl = useAppSelector(({baseUrl}) => baseUrl.value);
  const apiUrl = useAppSelector(({baseUrl}) => baseUrl.root || '/');

  useEffect(() => {
    let canceled = false;
    setFetching(true);
    setApiInfo(null);
    setError(null);

    fetchApiInfo(apiUrl)
      .then((info) => {
        if (canceled) {
          return;
        }
        setFetching(false);
        setError(null);
        setApiInfo(info);
      })
      .catch((e) => {
        if (canceled) {
          return;
        }
        setFetching(false);
        setError(e);
      });

    return () => {
      canceled = true;
    };
  }, [apiUrl]);

  return {
    fetching,
    info: apiInfo,
    url: gqlUrl,
    error,
  };
};

/**
 * Print the version information.
 */
export const VersionInfo = () => {
  const api = useApiInfo();
  const {showToast} = useToast();

  const data: Array<[string, string | null | undefined]> = [
    ['Web Version', NUDGE_VERSION],
    ['Web Release Date', NUDGE_RELEASE_DATE],
    ['API URL', api.url],
    ['API Core Version', api.info?.coreVersion],
    ['API Plugin Version', api.info?.pluginVersion],
    ['API Status', api.info?.status],
  ];
  const s = data.map(([label, value]) => `${label}: ${value}`).join('\n');

  return (
    <div
      id="nudge-version"
      onClick={() => copyVersion(s, showToast)}
      className="cursor-pointer hover:bg-(--muted) p-4 rounded-lg transition-colors"
    >
      {data.map(([label, value]) => (
        <div className="flex items-center justify-between py-1" key={label}>
          <Text className="font-medium text-color-(--foreground)">{label}</Text>
          <Text type="secondary" className="text-color-(--muted-accent)">
            {value}
          </Text>
        </div>
      ))}
    </div>
  );
};
